import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  CON_NOTE_EXPIRY_DAYS,
  CON_NOTE_REFUND_CLAIMED,
  CON_NOTE_REFUND_PENDING,
  CON_ORTC_RETURN,
  CON_ORTC_EXCHANGE,
  CON_STATUS_ACTIVE,
  CON_STATUS_DELETED,
} from "../sys/Constants";

dayjs.extend(customParseFormat);

export const NOTE_REFUND_LABEL = {
  CLAIMED: "Claimed",
  PENDING: "Pending",
  EXPIRED: "Expired",
};

const RETURN_DATE_FORMATS = ["MM-DD-YYYY", "YYYY-MM-DD", "DD-MM-YYYY"];

export function parseNoteReturnDate(dateStr) {
  if (!dateStr) return null;
  const raw = String(dateStr).trim();
  for (const fmt of RETURN_DATE_FORMATS) {
    const parsed = dayjs(raw, fmt, true);
    if (parsed.isValid()) return parsed;
  }
  const loose = dayjs(raw);
  return loose.isValid() ? loose : null;
}

/** True when return date + expiry days is before today (exclusive of expiry day). */
export function isNoteExpired(returnDateStr, expiryDays = CON_NOTE_EXPIRY_DAYS) {
  const returnDate = parseNoteReturnDate(returnDateStr);
  if (!returnDate) return false;
  const expiryDate = returnDate.add(expiryDays, "day").startOf("day");
  return dayjs().startOf("day").isAfter(expiryDate);
}

/** Claimed: REFUNDSTS R (or legacy A from cashier confirm). */
export function isNoteClaimed(refundSts) {
  const sts = String(refundSts || "").toUpperCase();
  return sts === CON_NOTE_REFUND_CLAIMED || sts === CON_STATUS_ACTIVE;
}

/**
 * Resolve display status for credit / exchange notes.
 * R = Claimed, P = Pending, Expired when return date + CON_NOTE_EXPIRY_DAYS passed.
 */
export function getNoteRefundStatus(record = {}) {
  const refundSts = record.REFUNDSTS ?? record.refundSts;
  const returnDate = record.RETURNDATE ?? record.returnDate;

  if (isNoteClaimed(refundSts)) {
    return {
      label: NOTE_REFUND_LABEL.CLAIMED,
      color: "success",
      code: CON_NOTE_REFUND_CLAIMED,
    };
  }

  if (isNoteExpired(returnDate)) {
    return {
      label: NOTE_REFUND_LABEL.EXPIRED,
      color: "error",
      code: "E",
    };
  }

  return {
    label: NOTE_REFUND_LABEL.PENDING,
    color: "warning",
    code: CON_NOTE_REFUND_PENDING,
  };
}

/** Pending (unredeemed): not claimed and not expired — same rule as Cashier search. */
export function isNoteUnredeemed(record = {}) {
  return getNoteRefundStatus(record).code === CON_NOTE_REFUND_PENDING;
}

/** From Orderreturn GET ["4"] — active pending credit / exchange notes. */
export function getUnredeemedCeNotes(headers = []) {
  const rows = Array.isArray(headers) ? headers : [];
  return rows.filter((row) => {
    const typeCode = Number(row.RETURNTYPECODE);
    return (
      (typeCode === CON_ORTC_RETURN || typeCode === CON_ORTC_EXCHANGE) &&
      String(row.STS || "").toUpperCase() !== CON_STATUS_DELETED &&
      isNoteUnredeemed({
        REFUNDSTS: row.REFUNDSTS,
        RETURNDATE: row.RETURNDATE,
      })
    );
  });
}

/** @deprecated Use getUnredeemedCeNotes */
export const getUnredeemedCreditNotes = getUnredeemedCeNotes;
